// Lazy loading and chunked data management for better performance
export interface DreamCharacter {
  id: string;
  character: string;
  title: string;
  description: string;
}

export interface RichContent {
  introduction?: string[];
  sections?: {
    headline: string;
    paragraph: string;
  }[];
  conclusion?: string;
}

export interface DreamObject {
  id: string;
  name: string;
  description: string;
  longDescription?: string;
  richContent?: RichContent;
  relatedCharacters: string[];
  isPopular?: boolean;
}

// Cache for loaded data chunks
const dataCache = new Map<string, any>();

// Lazy load only the data we need
const loadDataChunk = async (chunkType: 'characters' | 'objects' | 'popular'): Promise<any> => {
  if (dataCache.has(chunkType)) {
    return dataCache.get(chunkType);
  }

  // Load the full data only when needed
  const { default: dreamDataJson } = await import('./dreamData.json');
  
  let chunk;
  switch (chunkType) {
    case 'characters':
      chunk = dreamDataJson
        .filter(item => item.id && typeof item.id === 'string' && item.id.startsWith('char-'))
        .map(item => item as DreamCharacter);
      break;
    case 'objects':
      chunk = dreamDataJson
        .filter(item => item.id && typeof item.id === 'string' && item.id.startsWith('obj-'))
        .map(item => ({
          ...item,
          name: item.name.replace(/,\s*$/, ''), // Remove trailing comma
          description: item.description?.replace(/^[–-] /, '') || item.description, // Remove "– " or "- " prefix
          isPopular: ['გველი', 'სისხლი', 'ჯვარი', 'სასაფლაო', 'ოქრო', 'ცხენი', 'ეკლესია'].includes(item.name.replace(/,\s*$/, ''))
        } as DreamObject));
      break;
    case 'popular':
      const objects = dreamDataJson
        .filter(item => item.id && typeof item.id === 'string' && item.id.startsWith('obj-'))
        .map(item => ({
          ...item,
          name: item.name.replace(/,\s*$/, ''), // Remove trailing comma
          description: item.description?.replace(/^[–-] /, '') || item.description, // Remove "– " or "- " prefix
          isPopular: ['გველი', 'სისხლი', 'ჯვარი', 'სასაფლაო', 'ოქრო', 'ცხენი', 'ეკლესია'].includes(item.name.replace(/,\s*$/, ''))
        } as DreamObject));
      chunk = objects.filter(obj => obj.isPopular === true);
      break;
  }

  dataCache.set(chunkType, chunk);
  return chunk;
};

// Async getters
export const getCharacterData = () => loadDataChunk('characters');
export const getObjectData = () => loadDataChunk('objects');
export const getPopularDreams = () => loadDataChunk('popular');

// Enhanced utility function for URL formatting that handles all punctuation properly
export const formatObjectNameForUrl = (name: string): string => {
  if (!name) return '';
  
  // Remove all punctuation and normalize spaces, then convert to hyphens
  const formatted = name
    .replace(/[,().!?:;–—]/g, '') // Remove common punctuation including em dash and en dash
    .replace(/\s+/g, '-')         // Replace spaces with hyphens
    .replace(/--+/g, '-')         // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, '');     // Remove leading/trailing hyphens
  
  return formatted;
};

export const normalizeObjectName = (name: string): string => {
  if (!name) return '';
  try {
    const decoded = decodeURIComponent(name);
    // Convert hyphens back to spaces
    return decoded
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  } catch (e) {
    console.error("Error decoding object name:", e);
    return name.replace(/-/g, ' ').trim();
  }
};

export const getObjectByName = async (name: string): Promise<DreamObject | undefined> => {
  if (!name) return undefined;
  
  const objects = await getObjectData();
  const cleanName = name.trim().toLowerCase();
  
  // Debug logging for problematic cases
  console.log('=== DEBUG getObjectByName ===');
  console.log('Input name:', name);
  console.log('Clean name:', cleanName);
  
  // Try exact match first
  let obj = objects.find((obj: DreamObject) => obj.name.toLowerCase() === cleanName);
  if (obj) {
    console.log('Found exact match:', obj.name);
    return obj;
  }
  
  // Try with normalized name (dash to space)
  const normalizedName = normalizeObjectName(name).toLowerCase();
  console.log('Normalized name:', normalizedName);
  
  obj = objects.find((obj: DreamObject) => obj.name.toLowerCase() === normalizedName);
  if (obj) {
    console.log('Found normalized match:', obj.name);
    return obj;
  }
  
  // Enhanced matching patterns for names with punctuation
  const patterns = [
    // Remove all punctuation including em/en dashes
    normalizedName.replace(/[,().!?:;–—]/g, '').trim(),
    // Try adding trailing comma
    normalizedName + ',',
    // Handle em dash patterns (convert hyphens to em dash)
    normalizedName.replace(/-/g, '–'),
    normalizedName.replace(/-/g, '—'),
    // Handle the reverse: convert em dash to hyphen
    normalizedName.replace(/–/g, '-'),
    normalizedName.replace(/—/g, '-'),
    // Handle comma patterns
    normalizedName.replace(/\s+/g, ', '),
    normalizedName.replace(/\s/g, ', '),
    // Handle parentheses patterns  
    normalizedName.replace(/^([^-\s]+)-([^-\s]+)-([^-\s]+)$/, '$1 ($2, $3)'),
    normalizedName.replace(/^([^-\s]+)-([^-\s]+)$/, '$1 ($2)'),
    // Handle specific patterns like "რაიმეს-დაწერა" -> "რაიმეს დაწერა"
    normalizedName.replace(/^([^-\s]+)-([^-\s]+)(\s+.+)?$/, '$1, $2$3'),
    normalizedName.replace(/^([^-\s]+)-([^-\s]+)$/, '$1 $2'),
    // Remove all punctuation and normalize spaces
    normalizedName.replace(/[,().!?:;–—]/g, ' ').replace(/\s+/g, ' ').trim(),
  ];
  
  console.log('Trying patterns:', patterns);
  
  for (const pattern of patterns) {
    console.log('Trying pattern:', pattern);
    
    // Try exact match with pattern
    obj = objects.find((obj: DreamObject) => obj.name.toLowerCase() === pattern);
    if (obj) {
      console.log('Found pattern match:', obj.name, 'with pattern:', pattern);
      return obj;
    }
    
    // Try matching object names with punctuation removed against pattern
    obj = objects.find((obj: DreamObject) => {
      const objNameNoPunct = obj.name.toLowerCase().replace(/[,().!?:;–—]/g, '').trim();
      return objNameNoPunct === pattern;
    });
    if (obj) {
      console.log('Found punctuation-removed match:', obj.name, 'with pattern:', pattern);
      return obj;
    }
  }
  
  // Final fallback - partial matching with punctuation awareness
  return objects.find((obj: DreamObject) => {
    const objNameLower = obj.name.toLowerCase();
    const objNameNoPunct = objNameLower.replace(/[,().!?:;–—]/g, '').trim();
    const searchNoPunct = normalizedName.replace(/[,().!?:;–—]/g, '').trim();
    
    return objNameNoPunct.includes(searchNoPunct) || searchNoPunct.includes(objNameNoPunct);
  });
};

export const getCharacterByValue = async (character: string): Promise<DreamCharacter | undefined> => {
  const characters = await getCharacterData();
  const decodedCharacter = decodeURIComponent(character);
  
  const exactMatch = characters.find((char: DreamCharacter) => 
    char.character?.toLowerCase() === decodedCharacter?.toLowerCase()
  );
  
  if (exactMatch) return exactMatch;
  
  return characters.find((char: DreamCharacter) => 
    char.character?.toLowerCase().includes(decodedCharacter?.toLowerCase()) ||
    decodedCharacter?.toLowerCase().includes(char.character?.toLowerCase())
  );
};

export const getObjectsByCharacter = async (character: string): Promise<DreamObject[]> => {
  if (!character) return [];
  
  const objects = await getObjectData();
  const decodedCharacter = decodeURIComponent(character);
  
  return objects.filter((obj: DreamObject) => {
    const firstChar = obj.name.charAt(0);
    return firstChar.toLowerCase() === decodedCharacter.toLowerCase();
  });
};

export const searchObjects = async (query: string): Promise<DreamObject[]> => {
  if (!query || query.trim().length === 0) return [];
  
  const objects = await getObjectData();
  const lowercaseQuery = query.toLowerCase();
  
  return objects.filter((obj: DreamObject) => {
    return obj.name.toLowerCase().includes(lowercaseQuery);
  });
};
